import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { makeQueryClient } from "../api/queryClient";
import { ME_KEY } from "../api/hooks/useMe";
import { setUnauthorizedHandler } from "../api/client";
import { ToastProvider } from "../ui/Toast";
import { AuthGate } from "./AuthGate";
import { WorkspaceLayout } from "./WorkspaceLayout";
import { ErrorBoundary } from "./ErrorBoundary";
import { NotFound } from "./NotFound";
import { SignInPage } from "../auth/SignInPage";
import { SignUpPage } from "../auth/SignUpPage";
import { ForgotPasswordPage } from "../auth/ForgotPasswordPage";
import { ResetPasswordPage } from "../auth/ResetPasswordPage";
import { AcceptInvitePage } from "../workspace/AcceptInvitePage";
import { WorkspaceSettingsPage, GeneralSettings } from "../workspace/WorkspaceSettingsPage";
import { MembersPage } from "../workspace/MembersPage";
import { TrashPage } from "../workspace/TrashPage";
import { ReposPage } from "../repos/ReposPage";
import { TokensPage } from "../tokens/TokensPage";
import { AccountPage } from "../account/AccountPage";
import { RepoHomeRedirect } from "./RepoHomeRedirect";
import { RootRedirect } from "./RootRedirect";
import { DiffPage } from "../diff/DiffPage";
import { HistoryPage } from "../history/HistoryPage";
import { CommitPage } from "../history/CommitPage";
import { EditorRoute } from "../editor/route.js";
import { RepoBrowserPage } from "../repos/browser/RepoBrowserPage.js";
import { ProposalList } from "../proposals/ProposalList.js";
import { RepoSettingsPage } from "../repos/RepoSettings.js";

function buildRouter() {
  return createBrowserRouter([
    { path: "/sign-in", element: <SignInPage />, errorElement: <ErrorBoundary /> },
    { path: "/sign-up", element: <SignUpPage />, errorElement: <ErrorBoundary /> },
    { path: "/forgot-password", element: <ForgotPasswordPage />, errorElement: <ErrorBoundary /> },
    { path: "/reset-password/:token", element: <ResetPasswordPage />, errorElement: <ErrorBoundary /> },
    { path: "/invite/:token", element: <AcceptInvitePage />, errorElement: <ErrorBoundary /> },
    {
      element: (
        <AuthGate>
          <WorkspaceLayout />
        </AuthGate>
      ),
      errorElement: <ErrorBoundary />,
      children: [
        { path: "/", element: <RootRedirect /> },
        { path: "/account", element: <AccountPage /> },
        {
          // Studio app tree, at the ROOT namespace — a workspace URL is also
          // its git clone URL (the github.com model). The `_` sentinel
          // namespaces workspace/repo pages so repo names never collide with
          // them; a numeric segment after a repo is a changeset. `_` is the
          // only reserved ws/repo name.
          //
          // Top-level paths the static site tier serves (/docs, /viewer, …)
          // never reach this router — Traefik carves them out per
          // `deploy/routing.json`, which also generates the reserved-slug list
          // the server enforces so `:slug` can't shadow one.
          path: "/:slug",
          children: [
            { index: true, element: <ReposPage /> },
            {
              path: "_/settings",
              element: <WorkspaceSettingsPage />,
              children: [
                { index: true, element: <GeneralSettings /> },
                { path: "tokens", element: <TokensPage /> },
                { path: "trash", element: <TrashPage /> },
              ],
            },
            { path: "_/members", element: <MembersPage /> },
            {
              path: ":repo",
              children: [
                { index: true, element: <RepoHomeRedirect /> },
                { path: "_/diff", element: <DiffPage /> },
                { path: "_/proposals", element: <ProposalList /> },
                { path: "_/settings", element: <RepoSettingsPage /> },
                { path: "_/history/:ref", element: <HistoryPage /> },
                { path: "_/commit/:oid", element: <CommitPage /> },
                { path: "_/files/:ref/*", element: <RepoBrowserPage /> },
                // Changeset editor/review. `:num/*` matches both `/3` (no file)
                // and `/3/path/to.arch` (file open) — the `*` may be empty.
                { path: ":num/*", element: <EditorRoute /> },
              ],
            },
          ],
        },
      ],
    },
    { path: "*", element: <NotFound /> },
  ]);
}

// Unused export kept for compatibility — Navigate is re-exported in case
// consumers want it without importing react-router-dom directly.
export { Navigate };

export function AppRouter(){
  const client: QueryClient = useMemo(makeQueryClient, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      client.setQueryData(ME_KEY, null);
    });
    return () => setUnauthorizedHandler(null);
  }, [client]);

  const router = useMemo(buildRouter, []);

  return (
    <QueryClientProvider client={client}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
      {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  );
}
